import { areAddressesTheSame } from "@insite/client-framework/Common/CustomerSelectors";
import {
    Handler,
    HasOnSuccess,
    makeHandlerChainAwaitable,
    replaceInChain,
} from "@insite/client-framework/HandlerCreator";
import { Cart, CartResult, UpdateCartApiParameter } from "@insite/client-framework/Services/CartService";
import { createShipTo, updateBillTo, updateShipTo } from "@insite/client-framework/Services/CustomersService";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import loadCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCart";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import {
    AddOrUpdateShipTo,
    chain,
    LoadCart,
    UpdateBillTo,
} from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateCart";
import { BillToModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";

type HandlerType = Handler<
    HasOnSuccess,
    {
        shipTo?: ShipToModel;
        updatedBillTo?: BillToModel;
        updatedShipTo?: ShipToModel;
        cart: Cart;
        apiParameter: UpdateCartApiParameter;
        apiResult: CartResult;
    }
>;

export const UpdateBillToExtended: HandlerType = async props => {
    const { dispatch, getState, cart } = props;
    const state = getState();
    const {
        pages: {
            checkoutShipping: { billingAddressFormState },
        },
    } = state;
    if (!billingAddressFormState) {
        return;
    }

    const billTo = getBillToState(state, cart.billToId).value;

    if (!billTo) {
        throw new Error("There was no shipTo or billTo loaded for the current cart");
    }

    const { address: localModifiedBillTo } = billingAddressFormState;

    if (!areAddressesTheSame(billTo, localModifiedBillTo) && billTo.country?.name === "United States") {
        props.updatedBillTo = await updateBillTo({
            billTo: localModifiedBillTo,
            isEdit: true,
        });

        dispatch(loadBillTo({ billToId: billTo.id }));
        if (cart.shipToId === billTo.id) {
            dispatch(loadShipTo({ shipToId: cart.shipToId, billToId: billTo.id }));
        }

        props.apiParameter.cart.billToId = props.updatedBillTo.id;
    }
};

export const AddOrUpdateShipToExtended: HandlerType = async props => {
    const { dispatch, getState, cart } = props;
    const state = getState();
    const {
        pages: {
            checkoutShipping: { shippingAddressFormState },
        },
        context: {
            session: { fulfillmentMethod },
        },
    } = state;

    if (!shippingAddressFormState) {
        return;
    }

    const shipTo = getShipToState(state, cart.shipToId).value;
    const billTo = getBillToState(state, cart.billToId).value;
    if (!shipTo || !billTo) {
        throw new Error("There was no shipTo or billTo loaded for the current cart");
    }

    const { address: localModifiedShipTo } = shippingAddressFormState;

    if (!areAddressesTheSame(shipTo, localModifiedShipTo) && fulfillmentMethod !== "PickUp") {
        props.updatedShipTo = localModifiedShipTo.isNew
            ? await createShipTo({ shipTo: localModifiedShipTo })
            : await updateShipTo({ shipTo: localModifiedShipTo, billToId: billTo.id });

        const awaitableLoadShipTo = makeHandlerChainAwaitable(loadShipTo);
        await awaitableLoadShipTo({ shipToId: props.updatedShipTo.id, billToId: billTo.id })(dispatch, getState);

        props.apiParameter.cart.shipToId = props.updatedShipTo.id;
    }
};

export const LoadCartExtended: HandlerType = async props => {
    const { dispatch, getState } = props;
    const { cartId } = getState().pages.checkoutShipping;

    if (cartId) {
        const awaitableLoadCart = makeHandlerChainAwaitable(loadCart);
        await awaitableLoadCart({ cartId } as any)(dispatch, getState);
    } else {
        const awaitableLoadCurrentCart = makeHandlerChainAwaitable(loadCurrentCart);
        await awaitableLoadCurrentCart({ getPromotions: true })(dispatch, getState);
    }
};

replaceInChain(chain, UpdateBillTo, UpdateBillToExtended);
replaceInChain(chain, AddOrUpdateShipTo, AddOrUpdateShipToExtended);
replaceInChain(chain, LoadCart, LoadCartExtended);
